import React, {
  useEffect,
  useState,
} from "react";

import {
  Alert,
  Box,
  Button,
  Snackbar,
} from "@mui/material";

import {
  useNavigate,
} from "react-router-dom";

import NivelService from "../../../../../../services/LevelService";
import GradoService from "../../../../../../services/GradesService";
import DescargasService from "../../../../../../services/DownloadService";
import CompetenciaService from "../../../../../../services/CompetenciaService";

import useStep7Generation from "../../../../../../core/sesionGenerator/hooks/useStep7Generation";

import SessionBuildHeader from "./SessionBuildHeader";
import SessionBuildTable from "./SessionBuildTable";
import SessionGenerationDialog from "./SessionGenerationDialog";
import SessionDescriptionDialog from "./SessionDescriptionDialog";

import {
  step7Styles,
} from "./SessionBuildStep.styles";

import {
  LS_KEY,
  STEP_DESCRIPTIONS,
  STEP_LABELS,
  DEFAULT_ROW_VALUES,
  buildSessionPayload,
  buildSnapshot,
  enrichRowsWithMetadata,
  getIdPSQL,
  hasChanged,
  rowKey,
} from "./sessionBuildStep.helpers";

const SessionBuildStep = ({
  onBack,
  goSessionInput,
  unidadesSeleccionadas = [],
  userRole = "ADMIN",
}) => {
  const navigate = useNavigate();

  const [
    rows,
    setRows,
  ] = useState(
    unidadesSeleccionadas
  );

  const [
    openGeneration,
    setOpenGeneration,
  ] = useState(false);

  const [
    selectedRow,
    setSelectedRow,
  ] = useState(null);

  const [
    statusByRow,
    setStatusByRow,
  ] = useState({});

  const [
    activeMongoId,
    setActiveMongoId,
  ] = useState(null);

  const [
    descriptionRow,
    setDescriptionRow,
  ] = useState(null);

  const [
    openDescription,
    setOpenDescription,
  ] = useState(false);

  const [
    downloading,
    setDownloading,
  ] = useState({
    word: false,
    pdf: false,
    html: false,
  });

  const [
    errorMsg,
    setErrorMsg,
  ] = useState("");

  const [
    openError,
    setOpenError,
  ] = useState(false);

  const {
    progress,
    isGenerating,
    mongoId,
    error,
    currentMongoStep,
    startGeneration,
    resetProgress,
    restoreProgress,
    setExistingMongoId,
    getLatestProgress,
    markHtmlAsCompleted,
  } = useStep7Generation();


  // ============================================================
  // PROTEGER RECARGA DURANTE GENERACIÓN
  // ============================================================

  useEffect(() => {
    const handleBeforeUnload = (
      event
    ) => {
      if (!isGenerating) {
        return;
      }

      event.preventDefault();

      event.returnValue =
        "Se está generando una sesión. " +
        "Si sales ahora, perderás el progreso. " +
        "¿Estás seguro?";
    };

    window.addEventListener(
      "beforeunload",
      handleBeforeUnload
    );

    return () =>
      window.removeEventListener(
        "beforeunload",
        handleBeforeUnload
      );
  }, [isGenerating]);

  // ============================================================
  // RESTAURAR ESTADOS GUARDADOS
  // ============================================================

  useEffect(() => {
    try {
      const raw =
        localStorage.getItem(LS_KEY);

      setStatusByRow(
        raw
          ? JSON.parse(raw)
          : {}
      );
    } catch (storageError) {
      console.error(
        "No se pudo restaurar el estado de las sesiones:",
        storageError
      );

      localStorage.removeItem(
        LS_KEY
      );

      setStatusByRow({});
    }
  }, []);

  // ============================================================
  // CARGAR METADATOS DE LAS FILAS
  // ============================================================

  useEffect(() => {
    const loadRows = async () => {
      try {
        /*
         * enrichRowsWithMetadata debe conservar
         * todas las propiedades originales mediante
         * una copia con ...fila.
         *
         * De esa forma se conservan:
         *
         * fileId
         * archivoDocumento
         * origenDocumento
         */
        const nextRows =
          await enrichRowsWithMetadata(
            unidadesSeleccionadas,
            {
              listLevels:
                async () =>
                  (
                    await NivelService
                      .listarNiveles()
                  ).data || [],

              listGradesByLevel:
                async (nivelId) =>
                  (
                    await NivelService
                      .listarGradosPorNivel(
                        nivelId
                      )
                  ).data || [],

              listAreasByLevel:
                async (nivelId) =>
                  (
                    await GradoService
                      .listarAreasPorNivel(
                        nivelId
                      )
                  ).data || [],
            }
          );

        setRows(nextRows);
      } catch (loadError) {
        console.error(
          "Error cargando nombres de sesión:",
          loadError
        );

        setRows(
          unidadesSeleccionadas
        );
      }
    };

    loadRows();
  }, [unidadesSeleccionadas]);

  // ============================================================
  // PERSISTIR ESTADO DE UNA FILA
  // ============================================================

  const persistRowStatus = (
    key,
    nextValue
  ) => {
    let savedMap = {};

    try {
      const raw =
        localStorage.getItem(
          LS_KEY
        );

      savedMap =
        raw
          ? JSON.parse(raw)
          : {};
    } catch (storageError) {
      console.error(
        "No se pudo leer el estado guardado:",
        storageError
      );
    }

    savedMap[key] = nextValue;

    localStorage.setItem(
      LS_KEY,
      JSON.stringify(savedMap)
    );

    setStatusByRow(savedMap);
  };

  // ============================================================
  // CONSTRUIR PAYLOAD
  // ============================================================

  const buildPayload = async (
    row
  ) => {
    const idPSQL =
      getIdPSQL(row);

    if (!idPSQL) {
      throw new Error(
        "Sesión inválida " +
        "(id PostgreSQL faltante)"
      );
    }

    const competenciaTxt =
      row.competencia
        ? (
            await CompetenciaService
              .obtenerCompetenciaPorId(
                row.competencia
              )
          ).nombre
        : "";

    const capacidadesTxt =
      await Promise.all(
        (row.capacidades || []).map(
          async (id) =>
            (
              await CompetenciaService
                .obtenerCapacidadPorId(
                  id
                )
            ).descripcion
        )
      );

    const desempenosTxt =
      await Promise.all(
        (row.desempenos || []).map(
          async (id) =>
            (
              await CompetenciaService
                .obtenerDesempenioPorId(
                  id
                )
            ).descripcion
        )
      );

    /*
     * buildSessionPayload recibe la fila completa,
     * incluido row.fileId.
     *
     * El helper debe transformar:
     *
     * fila.fileId -> file_id
     */
    return buildSessionPayload({
      fila: row,
      competenciaTxt,
      capacidadesTxt,
      desempenosTxt,
    });
  };

  // ============================================================
  // GENERAR O VER SESIÓN
  // ============================================================

  const handleGenerate = async (
    row
  ) => {
    const idPSQL =
      getIdPSQL(row);

    if (!idPSQL) {
      setErrorMsg(
        "ID PostgreSQL faltante. " +
        "Regrese al paso anterior."
      );

      setOpenError(true);

      return;
    }

    const key =
      rowKey(row);

    const saved =
      statusByRow[key];

    const changed =
      hasChanged(
        row,
        saved?.snapshot
      );

    const isActuallyDone =
      saved?.progreso
        ?.html
        ?.estado ===
      "completado";

    setSelectedRow(row);

    /*
     * Si la sesión ya fue generada y no cambió,
     * solo se restaura para visualizarla.
     */
    if (
      saved &&
      !changed &&
      isActuallyDone
    ) {
      resetProgress();

      restoreProgress(
        saved.progreso
      );

      if (saved.mongoId) {
        setActiveMongoId(
          saved.mongoId
        );

        setExistingMongoId(
          saved.mongoId
        );
      }

      setOpenGeneration(true);

      return;
    }

    if (isGenerating) {
      return;
    }

    resetProgress();

    if (
      saved &&
      !changed &&
      !isActuallyDone
    ) {
      restoreProgress(
        saved.progreso
      );
    }

    setOpenGeneration(true);

    try {
      const payload =
        await buildPayload(row);

      const estandares =
        payload.estandares_ap;

      if (
        !estandares?.competencia?.trim() ||
        !Array.isArray(estandares.capacidades) ||
        estandares.capacidades.length === 0 ||
        !Array.isArray(estandares.desempenios) ||
        estandares.desempenios.length === 0
      ) {
        throw new Error(
          "La sesión no tiene una competencia, capacidades y desempeños completos. " +
          "Revise la selección curricular antes de generar."
        );
      }

      /*
       * No es necesario agregar file_id aquí
       * manualmente.
       *
       * buildSessionPayload lo incluye dentro de
       * payload.sesiones_data[0], y esta expansión
       * conserva el campo:
       *
       * ...payload.sesiones_data[0]
       */
      const generatedMongoId =
        await startGeneration({
          sesion: {
            ...payload
              .sesiones_data[0],

            id_PSQL:
              String(idPSQL),

            sesionGenerada_id:
              saved?.mongoId ||
              null,

            tipo_sesion:
              "SAC",

            // Conserva la selección curricular aprobada hasta
            // la petición final de generación.
            estandares_ap:
              estandares,
          },
        });

      if (!generatedMongoId) {
        return;
      }

      setActiveMongoId(
        generatedMongoId
      );

      await new Promise(
        (resolve) =>
          setTimeout(
            resolve,
            100
          )
      );

      persistRowStatus(
        key,
        {
          mongoId:
            generatedMongoId,

          id_PSQL:
            String(idPSQL),

          snapshot:
            buildSnapshot(row),

          progreso:
            getLatestProgress(),
        }
      );
    } catch (generationError) {
      console.error(
        "Error en handleGenerate:",
        generationError
      );

      setErrorMsg(
        generationError?.message ||
        "No se pudo generar la sesión."
      );

      setOpenError(true);
    }
  };

  // ============================================================
  // CERRAR DIÁLOGO
  // ============================================================

  const handleCloseGeneration = () => {
    if (isGenerating) {
      return;
    }

    setOpenGeneration(false);

    setTimeout(() => {
      resetProgress();
      setSelectedRow(null);
      setActiveMongoId(null);
    }, 0);
  };

  // ============================================================
  // DESCARGAR
  // ============================================================

  const handleDownload = async (
    type
  ) => {
    const id =
      activeMongoId ||
      mongoId;

    if (!id) {
      return;
    }

    try {
      setDownloading(
        (prev) => ({
          ...prev,
          [type]: true,
        })
      );

      await DescargasService
        .descargarArchivoSesion(
          id,
          "SAC",
          type,
          selectedRow
        );

      if (type === "html") {
        markHtmlAsCompleted();
      }
    } catch (downloadError) {
      console.error(
        `Error descargando ${type}:`,
        downloadError
      );
    } finally {
      setDownloading(
        (prev) => ({
          ...prev,
          [type]: false,
        })
      );
    }
  };

  // ============================================================
  // DESCRIPCIÓN
  // ============================================================

  const handleShowDescription = (
    row
  ) => {
    setDescriptionRow(row);
    setOpenDescription(true);
  };

  // ============================================================
  // NAVEGACIÓN
  // ============================================================

  const handleGoToSearch = () => {
    const rolePath = {
      ADMIN:
        "/admin/buscador",

      DIRECTOR:
        "/director/buscador",

      PROFESOR:
        "/profesor/buscador",
    };

    navigate(
      rolePath[userRole] ||
      rolePath.ADMIN
    );
  };

  // ============================================================
  // RENDER
  // ============================================================

  return (
    <Box
      className="step7-root"
      sx={step7Styles}
    >
      <SessionBuildHeader
        firstRow={rows[0]}
      />

      <SessionBuildTable
        rows={rows}
        statusByRow={statusByRow}
        isGenerating={isGenerating}
        rowKey={rowKey}
        hasChanged={hasChanged}
        onGenerate={handleGenerate}
        onShowDescription={
          handleShowDescription
        }
        defaultDescription={
          DEFAULT_ROW_VALUES
            .descripcion
        }
      />

      <Box
        className="step7-footer"
        sx={{
          mt: 3,
          width: "100%",
          justifyContent:
            "space-between",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          {typeof goSessionInput ===
            "function" && (
            <Button
              variant="outlined"
              onClick={
                goSessionInput
              }
              className={
                "step7-btn " +
                "step7-btn-back"
              }
            >
              Volver al inicio
            </Button>
          )}

          <Button
            variant="outlined"
            onClick={onBack}
            className={
              "step7-btn " +
              "step7-btn-back"
            }
          >
            Atrás
          </Button>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent:
              "flex-end",
            marginLeft: "auto",
          }}
        >
          <Button
            variant="contained"
            onClick={
              handleGoToSearch
            }
            className={
              "step7-btn " +
              "step7-btn-download"
            }
          >
            Ir al buscador
          </Button>
        </Box>
      </Box>

      <SessionGenerationDialog
        open={openGeneration}
        isGenerating={isGenerating}
        onClose={
          handleCloseGeneration
        }
        error={error}
        progress={progress}
        currentMongoStep={
          currentMongoStep
        }
        stepDescriptions={
          STEP_DESCRIPTIONS
        }
        stepLabels={
          STEP_LABELS
        }
        filaSeleccionada={
          selectedRow
        }
        sesionId={
          selectedRow
            ? getIdPSQL(selectedRow)
            : null
        }
        conComparativa={Boolean(
          selectedRow?.fileId ??
          selectedRow?.file_id
        )}
        downloading={downloading}
        onDownload={
          handleDownload
        }
      />

      <SessionDescriptionDialog
        open={openDescription}
        row={descriptionRow}
        onClose={() =>
          setOpenDescription(false)
        }
      />

      <Snackbar
        open={openError}
        autoHideDuration={4000}
        onClose={() =>
          setOpenError(false)
        }
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
      >
        <Alert
          severity="error"
          onClose={() =>
            setOpenError(false)
          }
        >
          {errorMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SessionBuildStep;
